import {useEffect, useReducer, useState} from 'react';
import AdminTable from '../AdminTable';
import {StyledAdminUsersTable} from '../styled';
import {useToast} from '../../../../../core/components/toast';
import {Column} from 'react-table';
import {Typography} from '../../../../../core/components';
import {colorTheme} from '../../../../../core/configs';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {useAuth} from '../../../../hooks/useAuth';
import {
  useDeleteDocumentAsAdmin,
  useGetEkycVerifiedUsers,
  usePatchRevokeEkycVerifications,
} from '../../../../server/react-query/useAdminUsers';
import {useHistory} from 'react-router-dom';

import {PaginatedUsersQueryParams} from '../../../../server/types/admin-control.types';
import { userVerificationStatus } from '../../../../server/types/user.verification.types';

interface TableData {
  userId: string;
  name: string;
  userType: string;
  idVerified: boolean;
  trueSelfieVerified: boolean;
}

export const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'SET_SEARCH':
      return {
        ...state,
        searchQuery: action.payload.search,
        page: 1,
      };
    case 'SET_PAGE':
      return {
        ...state,
        page: action.payload.page,
      };
    default:
      return state;
  }
};

export const INITIAL_SEARCH_STATE: PaginatedUsersQueryParams = {
  searchQuery: '',
  page: 1,
};

interface AdminEkycUsersTableProps {
  searchQuery: string;
}

const AdminEkycVerifiedUsersTable = (props: AdminEkycUsersTableProps) => {
  const history = useHistory();

  const {searchQuery: propSearchQuery} = props;

  const [pageCount, setPageCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [data, setData] = useState<TableData[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>(propSearchQuery);
  const [searchBody, dispatchSearch] = useReducer(
    reducer,
    INITIAL_SEARCH_STATE,
  );
  const {data: verifiedUsers, refetch} = useGetEkycVerifiedUsers(searchBody);

  const {systemLogEvent} = useAnalytics();
  const {authData} = useAuth();

  const {presentToast} = useToast();

  const [columns] = useState<Column<TableData>[]>([
    {
      Header: 'Name',
      accessor: 'name', // accessor is the "key" in the data
    },
    {
      Header: 'User ID',
      accessor: 'userId',
    },
    {
      Header: 'User Type',
      accessor: 'userType',
    },
    {
      Header: 'Verifications',
      accessor: 'verificaitons',
    },
  ] as Column<TableData>[]);

  useEffect(() => {
    refetch();
  }, [searchBody]);

  useEffect(() => {
    setPageNumber(1);
    setSearchQuery(propSearchQuery);
  }, [propSearchQuery]);

  useEffect(() => {
    dispatchSearch({
      type: 'SET_SEARCH',
      payload: {
        search: searchQuery?.trim(),
      },
    });
  }, [searchQuery]);

  const onPageChangedHandler = (pageNumber: number) => {
    setPageNumber(pageNumber);
    dispatchSearch({
      type: 'SET_PAGE',
      payload: {
        page: pageNumber,
      },
    });
  };

  useEffect(() => {
    if (verifiedUsers) {
      setPageCount(Math.ceil(verifiedUsers.total / verifiedUsers.pageSize));
      setData(
        verifiedUsers.data.map(i => {
          const userId = i.userId;
          const name = `${i.firstName} ${i.lastName}`;
          const idVerified = i.faceDocumentVerified;
          const trueSelfieVerified = i.trueSelfieVerified;

          const userType =
            i.userRole === 'homeowner' ? 'Homeowner' : 'Kasambahay';

          return {
            userId,
            name,
            userType,
            idVerified,
            trueSelfieVerified,
          } as TableData;
        }),
      );
    }
  }, [verifiedUsers]);

  const patchRevokeEkycVerifications = usePatchRevokeEkycVerifications();
  const deleteDocumentAsAdmin = useDeleteDocumentAsAdmin();
  const onChangeVerifiedStatus = async (
    rowData: TableData,
    userVerificationStatus: userVerificationStatus,
  ) => {
    try {
      await deleteDocumentAsAdmin.mutateAsync(rowData.userId);
      await patchRevokeEkycVerifications.mutateAsync({
        userId: rowData.userId,
        userVerificationStatus,
      });

      presentToast({
        message: 'Successfully updated.',
        variant: 'success',
        position: 'bottom',
      });

      await refetch();
    } catch (e) {
      console.log(e);
      presentToast({
        message: `Unexpected error was occurred.`,
        variant: 'error',
        position: 'bottom',
      });
    }
  };

  const manualRenderFn = (columnId: string, rowData: TableData) => {
    console.log('🚀 ~ manualRenderFn ~ rowData:', rowData);
    return (
      <div className="flex space-x-2">
        <div>
          <Typography
            label={`${rowData.idVerified ? 'ID Verified' : ''}${
              rowData.idVerified && rowData.trueSelfieVerified ? ' and ' : ''
            }${rowData.trueSelfieVerified ? 'True Selfie' : ''}`}
            variant="f2"
            color={colorTheme.dark}
            align="left"
          />
        </div>

        <div>|</div>

        <div
          className="cursor-pointer"
          onClick={() => onChangeVerifiedStatus(rowData, 'revoked')}>
          <Typography
            label="Revoke"
            variant="f2"
            color={colorTheme.primary}
            align="left"
          />
        </div>
      </div>
    );
  };

  return (
    <StyledAdminUsersTable>
      <AdminTable
        columns={columns}
        data={data}
        pageSize={10}
        pageCount={pageCount}
        pageNumber={pageNumber}
        showDebugView={false}
        onPageChange={onPageChangedHandler}
        manualRenderFn={manualRenderFn}
      />
    </StyledAdminUsersTable>
  );
};

export default AdminEkycVerifiedUsersTable;
